/**
 * actionKeyEndpoints — single source of truth for mapping each
 * `action_key` returned by GET /skip/jobs/:enc_id/available-actions
 * to its destination modal + endpoint contract.
 *
 * Phase 6B — replaces the hardcoded `(status, sub_flag) → button`
 * matrix in the deleted SkipJobActionButtons / SkipJobWorkflowModal.
 *
 * Three modal types:
 *   - 'collection'   → ProcessCollectionModal (existing, owns its own POST)
 *   - 'exchange'     → ProcessExchangeModal   (existing, dual-submit)
 *   - 'tip_return'   → ProcessTipReturnModal  (existing, dual-submit)
 *   - 'confirm'      → ConfirmActionModal     (new — generic confirm + notes)
 *
 * For 'confirm', the endpoint + method describe the API call. URL
 * placeholders are replaced at submit time:
 *   - ':enc_id' → the job's enc_id
 * Optional `body` is merged with the user's notes (notes wins).
 *
 * Unknown action_keys render a disabled button with a console.warn.
 */

export type ActionModalType =
  | 'confirm'
  | 'collection'
  | 'exchange'
  | 'tip_return'
  | 'mark_delivered';

export interface ActionKeyConfig {
  readonly modalType: ActionModalType;
  /** For 'confirm' only — URL template with ':enc_id' placeholder. */
  readonly endpoint?: string;
  readonly method?: 'PATCH' | 'POST';
  /** For 'confirm' only — fixed body fields merged with the user's notes. */
  readonly body?: Readonly<Record<string, unknown>>;
}

export const ACTION_KEY_ENDPOINTS: Readonly<Record<string, ActionKeyConfig>> = {
  // ----------------- Sub-flag REQUESTS (POST /:enc_id/<verb>) -----------------
  request_collection: {
    modalType: 'confirm',
    endpoint: '/skip/jobs/:enc_id/collection-request',
    method: 'POST',
  },
  request_exchange: {
    modalType: 'confirm',
    endpoint: '/skip/jobs/:enc_id/exchange-request',
    method: 'POST',
  },
  request_tip_return: {
    modalType: 'confirm',
    endpoint: '/skip/jobs/:enc_id/tip-return-request',
    method: 'POST',
  },

  // ----------------- Sub-flag COMPLETIONS — modal-specific -----------------
  complete_collection: { modalType: 'collection' },
  process_exchange: { modalType: 'exchange' },
  process_tip_return: { modalType: 'tip_return' },

  // ----------------- STAGE TRANSITIONS via PATCH /:enc_id/status -----------
  mark_complete: {
    modalType: 'confirm',
    endpoint: '/skip/jobs/:enc_id/status',
    method: 'PATCH',
    body: { new_status: 'completed' },
  },
  cancel: {
    modalType: 'confirm',
    endpoint: '/skip/jobs/:enc_id/status',
    method: 'PATCH',
    body: { new_status: 'cancelled' },
  },

  // ----------------- POSTPONE — POST /:enc_id/postpone -----------------
  postpone: {
    modalType: 'confirm',
    endpoint: '/skip/jobs/:enc_id/postpone',
    method: 'POST',
  },

  // ----------------- MARK DELIVERED — POST /:enc_id/actions/mark_delivered
  // (Phase 6G — backs the seed action added in migration 100 for
  //  delivery_only.new). Owns its own dedicated modal because the body
  //  carries optional delivered_at + driver_id_enc + notes.
  mark_delivered: {
    modalType: 'mark_delivered',
    endpoint: '/skip/jobs/:enc_id/actions/mark_delivered',
    method: 'POST',
  },

  // ----------------- MARK COLLECTED — PATCH /:enc_id/status to 'collected'
  // (Phase 6G — backs the seed action added in migration 100 for
  //  exchange.delivered + tip_and_return.delivered). Plain confirm
  //  modal — no extra fields. Server's existing status PATCH handles
  //  the inventory sync via Phase 6F.
  mark_collected: {
    modalType: 'confirm',
    endpoint: '/skip/jobs/:enc_id/status',
    method: 'PATCH',
    body: { new_status: 'collected' },
  },
};

/** Resolve a config; returns null for unknown keys (caller renders a disabled button). */
export function getActionKeyConfig(actionKey: string): ActionKeyConfig | null {
  return ACTION_KEY_ENDPOINTS[actionKey] ?? null;
}

export function buildActionUrl(template: string, jobEncId: string): string {
  return template.replace(':enc_id', encodeURIComponent(jobEncId));
}
